import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function CashBook() {
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [entries, setEntries] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadCashBook();
    }, [fromDate, toDate]);

    const loadCashBook = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getCashBook({
                from_date: fromDate,
                to_date: toDate
            });

            if (response.success) {
                setEntries(response.data || []);
                setMeta(response.meta || {});
            } else {
                setError(response.message || 'Failed to load cash book');
            }
        } catch (err) {
            console.error('Error loading cash book:', err);
            setError('An error occurred while loading the cash book');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Cash Book</h1>
                </div>
                <p className="text-sm text-slate-600">Cash account transactions</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading cash book...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!loading && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Voucher #</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Narration</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Receipt (₹)</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Payment (₹)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {entries.length > 0 ? (
                                    entries.map((entry) => (
                                        <tr key={entry.id} className="hover:bg-slate-50">
                                            <td className="px-6 py-4 text-sm text-slate-600">{entry.voucher_date}</td>
                                            <td className="px-6 py-4 text-sm font-medium text-slate-900">{entry.voucher_number}</td>
                                            <td className="px-6 py-4 text-sm text-slate-600">{entry.narration || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-emerald-600">
                                                {parseFloat(entry.debit_amount) > 0 ? `₹${parseFloat(entry.debit_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-rose-600">
                                                {parseFloat(entry.credit_amount) > 0 ? `₹${parseFloat(entry.credit_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-slate-400 italic">No cash transactions in this period</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CashBook;
